import React, { useState } from "react";
import { Modal, Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import OtpInput from "react-otp-input";
import './Modal2FA.scss';

const Modal2FA = ({ show, onClose, mfaTokenData, activated, onSuccess, onError }) => {
  const [otp, setOtp] = useState("");
  const [copied, setCopied] = useState(false);
  const [askForRecovery, setAskForRecovery] = useState(false);
  const [inputRecoveryCode, setInputRecoveryCode] = useState("");

  const copiarRecoveryCode = () => {
    if (!mfaTokenData?.recoveryCode) return;
    navigator.clipboard.writeText(mfaTokenData.recoveryCode)
      .then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 3000);
      })
      .catch(() => alert("No se pudo copiar el código"));
  };

  const verificar = async (payload) => {
    try {
      const endpoint = activated
        ? `${process.env.REACT_APP_API_URL}/apis/user/2FA/validate`
        : `${process.env.REACT_APP_API_URL}/apis/user/2FA/activate`;

      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...payload })
      });

      const data = await res.json();
      const dataStatus = data.error ?? data.state
      if (dataStatus === "OK") {
        onSuccess();
      } else {
        onError(data.errorDetalle);
      }

    } catch (err) {
      onError("Error de conexión. Intenta nuevamente.");
    }
  };

  const handleOtpChange = (value) => {
    setOtp(value);
    if (value.length === 6) {
      verificar({ code: value, idUser: mfaTokenData?.id });
    }
  };

  const handleRecoveryVerify = () => {
    if (!inputRecoveryCode.trim()) {
      return;
    }
    verificar({ recoveryCode: inputRecoveryCode, idUser: mfaTokenData?.id });
  };

  const renderOtpInput = (inputProps) => (
    <input
      {...inputProps}
      style={{
        width: "3rem",
        height: "3rem",
        margin: "0 0.25rem",
        fontSize: "1.8rem",
        borderRadius: 4,
        border: "1px solid #ccc",
        textAlign: "center"
      }}
    />
  );

  return (
    <Modal show={show} onHide={onClose} centered dialogClassName="modal-auto-width" size="md" animation={false} >
      <Modal.Header closeButton>
        <Modal.Title>
          <h3 className="text-blue-500 fw-medium">{activated
            ? (askForRecovery ? "Recuperar acceso" : "Verificar segundo factor de autenticación")
            : "Activar segundo factor de autenticación"}
          </h3>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* 2FA activado */}
        {activated && !askForRecovery && (
          <>
            <h5>Ingrese el código de 6 dígitos de la app:</h5>
            <div className="d-flex justify-content-center mt-4">
              <OtpInput
                value={otp}
                onChange={handleOtpChange}
                numInputs={6}
                isInputNum
                renderInput={renderOtpInput}
              />
            </div>
            <p className="mt-3">
              Si perdiste acceso a tu dispositivo hacé click{" "}
              <span
                style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setAskForRecovery(true)}
              >
                aquí
              </span>.
            </p>
          </>
        )}

        {/* Recuperación */}
        {activated && askForRecovery && (
          <>
            <div className="input-group mb-3">
              <input
                type="text"
                className="form-control"
                placeholder="Ingresa tu código de recuperación"
                value={inputRecoveryCode}
                onChange={(e) => setInputRecoveryCode(e.target.value)}
              />
              <Button onClick={handleRecoveryVerify} disabled={!inputRecoveryCode.trim()}>Verificar</Button>
            </div>
            <p>
              Si no tienes acceso al código de recuperación, contacta a un administrador.
            </p>
            <p>
              Para volver a la pantalla anterior haz click{" "}
              <span
                style={{ color: "#007bff", cursor: "pointer", textDecoration: "underline" }}
                onClick={() => setAskForRecovery(false)}
              >
                aquí
              </span>.
            </p>
          </>
        )}

        {/* 2FA no activado */}
        {!activated && (
          <>
            <p className="text-muted mb-2">
              Para activar el segundo factor, descargue una app de autenticación.
            </p>

            <div className="qrToken d-flex flex-column gap-2 mt-3">
              <div className="qrToken__containerQr" style={{ flex: 1 }}>
                <h5>1. Escanee el código QR</h5>
                <div>
                  <img src={mfaTokenData?.qrCode} alt="QR MFA"/>
                </div>
              </div>
              <div className="qrToken__containerToken d-flex flex-column" style={{ flex: 1 }}>
                <p className="text-muted m-0 mb-2">O ingrese manualmente el código:</p>
                <h4 className="token">{mfaTokenData?.secretKey}</h4>
              </div>
            </div>

            <div className="mt-3">
              <h5>
                  2. Copie su código de recuperación{" "}
                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="tooltip-recovery" className="custom-tooltip">
                        Este es tu código de recuperación de MFA. Solo debe usarse si pierdes acceso a tu app de autenticación. Es secreto y nunca debe compartirse con nadie. Se mostrará una sola vez; guárdalo en un lugar seguro.
                      </Tooltip>
                    }
                  >
                    <i className="bi bi-info-circle ms-1 text-blue-500" style={{ cursor: "pointer"}}></i>
                  </OverlayTrigger>
              </h5>

              <div className="recovery-code-container d-flex flex-column align-items-start">
                <div className="d-flex flex-row">
                    <code className="p-2 bg-light border me-2">
                      {mfaTokenData?.recoveryCode}
                    </code>

                    <Button className="rounded-pill px-3" size="sm" onClick={copiarRecoveryCode}>
                      Copiar
                    </Button>
                </div>
                {copied && (
                  <span className="copied-message">
                    ✅ Código copiado!
                  </span>
                )}
              </div>
            </div>

            <div className="mt-3">
              <h5>3. Ingrese el código de 6 dígitos</h5>
              <div className="d-flex justify-content-start mt-3">
                <OtpInput
                  value={otp}
                  onChange={handleOtpChange}
                  numInputs={6}
                  isInputNum
                  renderInput={renderOtpInput}
                />
              </div>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default Modal2FA;
